import React, { useEffect, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ARCGIS_API, SIGNAGE_MEDIA } from '../../utils/constants';
import API from '../../utils/API';
import { timezome } from '../../utils/constants';
import DropdownSelect from './DropdownSelect';
import commonFunctions from '../../utils/commonFunctions';
import RadioCheck from '../MessageManager/RadioCheck';

const Calendar = ({
  setSuccess,
  closeModal,
  refreshData,
  data = [],
  calendarData = {},
  zone = "",
  setZone,
  setZoneData,
  updatePlaylistsMedia,
  markUnsavedChanges
}) => {

  const [timezone, setTimezone] = useState('Pacific/Pago_Pago');
  const [modalError, setModalError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const timezoneOptions = Object.entries(timezome).map(([value, label]) => ({
    value,
    label
  }));
  const [calendarType, setCalendarType] = useState(0); 
  const [calendarUrl, setCalendarUrl] = useState('');
  const [calendarUrlValid, setCalendarUrlValid] = useState("");
  const intl = useIntl();

  /**
    * Reset modal
    */
  function resetModal() {
    setSubmitted(false);
    setModalError('');
    if (setZone)
      setZone('');
  }

  useEffect(() => {
    if (zone !== SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET1&&zone !== SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET2) return;
    if (calendarData && calendarData.content && calendarData.content.paramters) {
      const calendarParameters = calendarData.content.paramters;
      setTimezone(calendarParameters.timeZone || 'Pacific/Pago_Pago');
      setCalendarType(calendarParameters.calendarType || 0);
      setCalendarUrl(calendarParameters.calendarUrl || '');
    }
  }, [calendarData, zone]);

  useEffect(() => {
    if (calendarType === 0) {
      setCalendarUrlValid(""); 
      return;
    }

    const trimmedUrl = calendarUrl.trim();

    if (!trimmedUrl) {
      setCalendarUrlValid("EMPTY_FIELD_ERROR");
      return;
    }

    const urlPattern = /^https:\/\/.+$/;
    setCalendarUrlValid(
      urlPattern.test(trimmedUrl)
        ? "" 
        : "INVALID_CALENDAR_URL"
    );
  }, [calendarUrl, calendarType]);

  useEffect(() => {
    if (!calendarUrlValid) {
      setModalError("");
    }
  }, [calendarUrlValid]);

  useEffect(() => {
    if (Object.keys(data).length > 0 && data.id) {
      const clockParameters = data.content.paramters;
      setTimezone(clockParameters.timeZone || 'Pacific/Pago_Pago');
      setCalendarType(clockParameters.calendarType || 0);
      setCalendarUrl(clockParameters.calendarUrl || '');
    }
  }, [data]);

  useEffect(() => {
    const modalElement = document.getElementById('calendarModal');
    modalElement.addEventListener('hidden.bs.modal', resetModal);

    return () => {
      modalElement.removeEventListener('hidden.bs.modal', resetModal);
    };
  }, []);

  const handleAdd = async () => {
    try {
      if (calendarType === 1 && calendarUrlValid) {
        setModalError(<FormattedMessage id="SUBMIT_ERROR" />);
        return;
      }
      if (zone) {
        setZoneData(prevCalendarData => {
          const newCalendarData = { ...prevCalendarData };
          newCalendarData[zone] = {
            zone: zone,
            content: {
              category: SIGNAGE_MEDIA.MEDIA_CATEGORY.WIDGET,
              subcategory: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.CALENDAR,
              type: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.CALENDAR,
              name: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.CALENDAR,
              paramters: {
                timeZone: timezone,
                calendarType: calendarType,
                calendarUrl: calendarUrl
              }
            }
          };
          return newCalendarData;
        });
      } else {
        const formData = {
          category: SIGNAGE_MEDIA.MEDIA_CATEGORY.WIDGET,
          subcategory: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.CALENDAR,
          type: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.CALENDAR,
          name: "",
          content: {
            paramters: {
              timeZone: timezone,
              calendarType: calendarType,
              calendarUrl: calendarUrl
            }
          }
        };
        if (Object.keys(data).length > 0 && data.id) {
          formData.id = data.id;
          await API.updateMedia(formData);
          updatePlaylistsMedia(formData);
          setSuccess(<FormattedMessage id="EDIT_CALENDAR_SUCCESS" />);
        } else {
          await API.addMedia(formData);
          setSuccess(<FormattedMessage id="ADD_CALENDAR_SUCCESS" />);
        }
        markUnsavedChanges && markUnsavedChanges();
        refreshData();
      }
      closeModal("calendarModal");
    } catch (err) {
      const errorId = Object.keys(data).length > 0 && data.id ? "EDIT_CALENDAR_FAILED" : "ADD_CALENDAR_FAILED";
      setModalError(<FormattedMessage id={errorId} values={{ errorMsg: <b>{err.message}</b> }} />);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    handleAdd();
  };

  return (
    <div className='modal modal-mse fade' id='calendarModal' tabIndex='-1' aria-labelledby='calendarModalLabel' aria-hidden='true'>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content modal-mes-content' style={{ overflow: 'visible' }}>
          <div className='modal-header modal-mes-header' style={{ backgroundColor: '#347BFA' }}>
            <h5 className='modal-title modal-mes-title'><FormattedMessage id='CALENDAR' /></h5>
            <button id="closeModal" type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close'></button>
          </div>
          <div className='modal-body' style={{ overflow: 'visible' }}>
            <form onSubmit={handleSubmit}>
              <div className='row'>
                <div className="col-12">
                  <label className="font-weight-600 font-size-14 mb-2"><FormattedMessage id="TIMEZONE" defaultMessage="Timezone" />:</label>
                  <DropdownSelect
                    options={timezoneOptions}
                    value={timezone}
                    onChange={setTimezone}
                  />
                </div>
                <div className="mb-1 col-12">
                  <div>
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input mes-radio"
                        type="radio"
                        name="calendarType"
                        id="genericCalendar"
                        value="generic"
                        checked={calendarType === 0}
                        onChange={() => setCalendarType(0)}
                      />
                      <label className="form-check-label font-weight-600 font-size-14" htmlFor="genericCalendar">
                        <FormattedMessage id="GENERIC_CALENDAR" defaultMessage="Generic Calendar" />
                      </label>
                    </div>
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input mes-radio"
                        type="radio"
                        name="calendarType"
                        id="schoolCalendar"
                        value="school"
                        checked={calendarType === 1}
                        onChange={() => setCalendarType(1)}
                      />
                      <label className="form-check-label font-weight-600 font-size-14" htmlFor="schoolCalendar">
                        <FormattedMessage id="SCHOOL_WORK_CALENDAR" defaultMessage="School/Work Calendar URL" />:
                      </label>
                    </div>
                  </div>
                </div>
                {
                  <div className="mb-3 col-12" style={{ paddingLeft: 20 }}>
                    <input
                      type="text"
                      className={`form-control me-2 h-54 bg-baby-blue ${submitted && (calendarUrlValid ? ' is-invalid' : ' is-valid')}`}
                      value={calendarUrl}
                      onChange={(e) => setCalendarUrl(e.target.value)}
                      disabled={calendarType === 0}
                      placeholder={intl.formatMessage({ id: 'ENTER_CALENDAR_URL', defaultMessage: 'Enter Calendar URL' })}
                    />
                    {commonFunctions.InvalidFeedbackString("", 'SCHOOL_WORK_CALENDAR')}
                  </div>
                }
              </div>
              {submitted && modalError && (
                <div className='modal-error invalid-feedback d-block fw-bold text-center mt-3'>
                  {modalError}
                </div>
              )}
            </form>
          </div>
          <div className='modal-footer modal-mes-footer d-flex align-items-center'>
            <button id="cancelBtn" type='button' className='btn btn-outline-secondary me-3' data-bs-dismiss='modal'>
              <FormattedMessage id='CANCEL' />
            </button>
            <button id="saveGroupBtn" className='btn btn-primary but-darkblue' type='button' onClick={handleSubmit}>
              <FormattedMessage id='ADD_CALENDAR' />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
